package com.sddamico.draftmachine.pages.pools

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.LoadingSection
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Badge
import com.sddamico.draftmachine.ds.core.BadgeTone
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.core.Divider
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.forms.Select
import com.sddamico.draftmachine.ds.forms.Switch
import com.sddamico.draftmachine.ds.forms.Textarea
import com.sddamico.draftmachine.ds.motion.Presence
import com.sddamico.draftmachine.ds.overlays.Modal
import com.sddamico.draftmachine.ds.overlays.ModalSize
import com.sddamico.draftmachine.ds.roleH1
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.models.CreatePoolRequest
import com.sddamico.draftmachine.models.NewPlayerEntry
import com.sddamico.draftmachine.models.PlayerPoolResponse
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.queryParam
import com.sddamico.draftmachine.util.rememberAuthState
import com.sddamico.draftmachine.util.sportLabel
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input as DomInput
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get

private const val MIXED = ""

/**
 * Forge a pool (frames 1e–1g): one page — nameplate, community switch, players (typed, pooled,
 * or CSV) — with the content check running at create time (a 422 renders the blocked banner and
 * flags the offending field). `?return=<path>` sends the new pool id back to the caller
 * (`<path>?poolId=<id>`), used by the draft wizard's "Create a new pool" door.
 */
@Page("new")
@Composable
fun PoolNewPage() {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()

  when (val s = auth.value) {
    is LoadState.Loading -> LoadingSection(size = 48.px, padding = 60.px)
    is LoadState.Failed -> { LaunchedEffect(Unit) { ctx.router.transitionTo("/login") } }
    is LoadState.Ready -> {
      if (s.value == null) {
        LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      } else {
        PoolForge()
      }
    }
  }
}

@Composable
private fun PoolForge() {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()
  val returnPath = remember { queryParam("return").takeIf { it == "/draft/new" } }

  var name by remember { mutableStateOf("") }
  var description by remember { mutableStateOf("") }
  var sportKey by remember { mutableStateOf(MIXED) }
  var isPublic by remember { mutableStateOf(false) }
  var players by remember { mutableStateOf(listOf<NewPlayerEntry>()) }
  var childPools by remember { mutableStateOf(listOf<PlayerPoolResponse>()) }

  var playerEntry by remember { mutableStateOf("") }
  var entryKey by remember { mutableStateOf(0) }
  var entrySportKey by remember { mutableStateOf(MIXED) }

  var pickerOpen by remember { mutableStateOf(false) }
  var csvFile by remember { mutableStateOf<Pair<String, ParsedCsv>?>(null) }
  var dragActive by remember { mutableStateOf(false) }
  val fileInput = remember { arrayOfNulls<HTMLInputElement>(1) }

  var submitting by remember { mutableStateOf(false) }
  var errorMessage by remember { mutableStateOf<String?>(null) }
  var errorField by remember { mutableStateOf<String?>(null) }

  val sport: Sport? = sportKey.takeIf { it != MIXED }?.let { runCatching { Sport.valueOf(it) }.getOrNull() }
  val entrySport: Sport? = sport ?: entrySportKey.takeIf { it != MIXED }?.let { runCatching { Sport.valueOf(it) }.getOrNull() }

  fun readFile(file: File) {
    val reader = FileReader()
    reader.onload = {
      val parsed = (reader.result as? String)?.let(::parsePoolCsv)
      if (parsed != null) csvFile = file.name to parsed
    }
    reader.readAsText(file)
  }

  fun close() {
    ctx.router.transitionTo(returnPath ?: "/pools")
  }

  fun submit() {
    if (submitting || name.isBlank()) return
    submitting = true
    errorMessage = null
    errorField = null
    scope.launch {
      try {
        val created = apiPost(
          "/api/player-pools",
          apiJson.encodeToString(
            CreatePoolRequest.serializer(),
            CreatePoolRequest(
              name = name.trim(),
              description = description.trim().ifBlank { null },
              sport = sport,
              isPublic = isPublic,
              players = players,
              childPoolIds = childPools.map { it.id },
            ),
          ),
          PlayerPoolResponse.serializer(),
        )
        if (returnPath != null) {
          ctx.router.transitionTo("$returnPath?poolId=${created.id}")
        } else {
          ctx.router.transitionTo("/pools/${created.id}")
        }
      } catch (e: ApiException) {
        errorMessage = e.errorMessage
        errorField = e.field
        submitting = false
        window.scrollTo(0.0, 0.0)
      } catch (e: Throwable) {
        errorMessage = UNREACHABLE_MESSAGE
        submitting = false
      }
    }
  }

  fun addPlayer() {
    val entry = parsePlayerEntry(playerEntry, entrySport)
    if (entry != null && players.none { it.name.equals(entry.name, ignoreCase = true) }) {
      players = players + entry
    }
    playerEntry = ""
    entryKey++
  }

  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = {
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
          .height(56.px).padding(leftRight = 16.px)
          .borderBottom(1.px, LineStyle.Solid, DmColor.border).toAttrs()
      ) {
        IconButton(
          icon = "x", label = "Close", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
          onClick = ::close,
        )
        SpanText("New pool", Modifier.dmStamp().color(DmColor.textMuted))
      }
    },
  ) {
    Div(
      attrs = Modifier.flex(1).displayFlex().flexDirection(FlexDirection.Column).gap(14.px)
        .padding(top = 20.px, leftRight = 16.px, bottom = 8.px).toAttrs()
    ) {
      SpanText("Create a pool", Modifier.roleH1().fontSize(24.px).color(DmColor.text))

      // Content-check failure banner (frame 1g).
      if (errorMessage != null) {
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.FlexStart).gap(12.px).padding(14.px)
            .borderRadius(DmEffects.radiusMd)
            .border(1.px, LineStyle.Solid, DmColor.rust500)
            .backgroundColor(Color.rgba(180, 71, 42, 0.08f)).toAttrs()
        ) {
          Icon("hand-palm", IconWeight.Fill, Modifier.fontSize(20.px).color(DmColor.rust300).margin(top = 1.px))
          Div {
            SpanText("The stamp didn't clear", Modifier.roleH3().fontSize(14.px).color(DmColor.rust300).displayBlock())
            SpanText(
              "$errorMessage. Blocked for public and private pools alike — fix it and forge again.",
              Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textSecondary)
                .lineHeight(1.5).displayBlock().margin(top = 3.px),
            )
          }
        }
      }

      // Nameplate.
      Div(attrs = Modifier.poolPanel().toAttrs()) {
        SpanText("Nameplate", Modifier.dmStamp())
        Input(
          label = "Pool name", icon = "tag", value = name,
          error = errorMessage?.takeIf { errorField == "name" },
          onValueChange = { name = it },
        )
        Textarea(
          label = "Description · optional", placeholder = "What belongs in here, and why?",
          value = description, rows = 2,
          error = errorMessage?.takeIf { errorField == "description" },
          onValueChange = { description = it },
        )
        Select(label = "Sport · optional", value = sportKey, onValueChange = { sportKey = it }) {
          Option(value = MIXED) { Text("Mixed · any sport") }
          Sport.entries.forEach { sp -> Option(value = sp.name) { Text(sportLabel(sp)) } }
        }
      }

      // Community listing.
      Div(attrs = Modifier.poolPanel().toAttrs()) {
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.SpaceBetween).gap(12.px).toAttrs()
        ) {
          Div {
            SpanText("List it for the community", Modifier.roleH3().fontSize(15.px).displayBlock())
            SpanText(
              "Anyone can use or clone it.",
              Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted)
                .displayBlock().margin(top = 2.px),
            )
          }
          Switch(checked = isPublic, onChange = { isPublic = it })
        }
      }

      // Players.
      Div(attrs = Modifier.poolPanel().toAttrs()) {
        Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Baseline).justifyContent(JustifyContent.SpaceBetween).toAttrs()) {
          SpanText("Add players", Modifier.dmStamp())
          if (players.isNotEmpty()) {
            SpanText("${players.size} so far", Modifier.dmStamp().color(DmColor.brass400))
          }
        }
        if (errorField == "players" && errorMessage != null) {
          SpanText(
            errorMessage!!,
            Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.rust400),
          )
        }
        if (sport == null) {
          Select(
            label = "Sport for typed players",
            value = entrySportKey,
            onValueChange = { entrySportKey = it },
          ) {
            Option(value = MIXED) { Text("Pick a sport") }
            Sport.entries.forEach { sp -> Option(value = sp.name) { Text(sportLabel(sp)) } }
          }
        }
        Div(attrs = Modifier.displayFlex().gap(8.px).toAttrs()) {
          key(entryKey) {
            Input(
              placeholder = playerEntryPlaceholder(entrySport), icon = "user",
              value = playerEntry, onValueChange = { playerEntry = it },
              autoFocus = entryKey > 0,
              onEnter = ::addPlayer,
              modifier = Modifier.flex(1),
            )
          }
          IconButton(
            icon = "plus", label = "Add player", variant = ButtonVariant.Secondary,
            disabled = playerEntry.isBlank() || entrySport == null,
            onClick = ::addPlayer,
          )
        }
        if (players.isNotEmpty()) {
          Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(6.px).toAttrs()) {
            players.takeLast(8).reversed().forEach { p ->
              PoolPlayerRow(
                p.sport, p.name, listOfNotNull(p.position, p.team).joinToString(" · "),
                onRemove = { players = players.filterNot { it === p } },
              )
            }
            if (players.size > 8) {
              SpanText(
                "Showing latest 8 of ${players.size}",
                Modifier.dmStamp().color(DmColor.steel500).textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center),
              )
            }
          }
        }

        Divider(label = "or")

        // Add a whole pool.
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px)
            .padding(topBottom = 12.px, leftRight = 14.px)
            .border(1.px, LineStyle.Dashed, DmColor.borderStrong).borderRadius(DmEffects.radiusMd)
            .cursor(Cursor.Pointer)
            .toAttrs {
              attr("role", "button")
              onClick { pickerOpen = true }
            }
        ) {
          Icon("stack-plus", IconWeight.Bold, Modifier.fontSize(20.px).color(DmColor.brass400))
          Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
            SpanText("Add a whole pool", Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold).color(DmColor.text).displayBlock())
            SpanText(
              "Official or community — nested pools stay linked.",
              Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted).displayBlock(),
            )
          }
        }
        childPools.forEach { child ->
          ChildPoolRow(child, onRemove = { childPools = childPools.filterNot { it.id == child.id } })
        }

        // CSV drop zone.
        Div(
          attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).alignItems(AlignItems.Center).gap(6.px)
            .padding(topBottom = 18.px, leftRight = 14.px)
            .border(1.px, if (dragActive) LineStyle.Solid else LineStyle.Dashed, if (dragActive) DmColor.brass400 else DmColor.borderStrong)
            .borderRadius(DmEffects.radiusMd)
            .backgroundColor(DmColor.surfaceInset)
            .cursor(Cursor.Pointer)
            .toAttrs {
              attr("role", "button")
              attr("aria-label", "Upload a players CSV")
              onClick { fileInput[0]?.click() }
              onDragOver { it.preventDefault(); dragActive = true }
              onDragEnter { it.preventDefault(); dragActive = true }
              onDragLeave { dragActive = false }
              onDrop { ev ->
                ev.preventDefault()
                dragActive = false
                ev.dataTransfer?.files?.get(0)?.let(::readFile)
              }
            }
        ) {
          Icon("upload-simple", IconWeight.Bold, Modifier.fontSize(22.px).color(DmColor.glass400))
          SpanText(
            "Drop a CSV here, or browse",
            Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold).color(DmColor.text),
          )
          SpanText(
            if (sport == null) {
              "Columns: name + sport required · team/position optional"
            } else {
              "Columns: name required · team/position optional"
            },
            Modifier.dmStamp().color(DmColor.steel500),
          )
        }
        DomInput(
          type = InputType.File,
          attrs = {
            attr("accept", ".csv,text/csv")
            style { property("display", "none") }
            ref { el ->
              val input = el as HTMLInputElement
              fileInput[0] = input
              val listener: (Event) -> Unit = { input.files?.get(0)?.let(::readFile); input.value = "" }
              input.addEventListener("change", listener)
              onDispose {
                input.removeEventListener("change", listener)
                fileInput[0] = null
              }
            }
          },
        )
      }
    }

    // Footer.
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px)
        .padding(16.px).margin(top = 14.px)
        .borderTop(1.px, LineStyle.Solid, DmColor.border).toAttrs()
    ) {
      Button(
        "Create the pool", variant = ButtonVariant.Primary, size = ButtonSize.Lg,
        icon = "hammer", fullWidth = true,
        loading = submitting, disabled = name.isBlank(),
        onClick = ::submit,
      )
      SpanText(
        forgeSummary(players.size, childPools.size),
        Modifier.dmStamp().color(DmColor.steel500).textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center),
      )
    }
  }

  Presence(pickerOpen) {
    PoolPickerModal(
      excludeIds = childPools.map { it.id }.toSet(),
      onPick = { childPools = childPools + it; pickerOpen = false },
      onDismiss = { pickerOpen = false },
    )
  }
  Presence(csvFile) { (fileName, parsed) ->
    PoolCsvMappingModal(
      fileName = fileName,
      csv = parsed,
      fallbackSport = sport,
      existingKeys = players.map { it.name.lowercase() }.toSet(),
      onImport = { imported ->
        players = players + imported
        csvFile = null
      },
      onDismiss = { csvFile = null },
    )
  }
}

private fun forgeSummary(playerCount: Int, poolCount: Int): String {
  val parts = mutableListOf<String>()
  parts += "$playerCount player${if (playerCount == 1) "" else "s"}"
  if (poolCount > 0) parts += "$poolCount pool${if (poolCount == 1) "" else "s"}"
  return parts.joinToString(" + ") + " · content check runs now"
}

/**
 * Browse-and-pick modal for nesting a pool: official, community, and your pools in one searchable
 * list (already-added [excludeIds] are hidden).
 */
@Composable
fun PoolPickerModal(
  excludeIds: Set<String>,
  onPick: (PlayerPoolResponse) -> Unit,
  onDismiss: () -> Unit,
) {
  var pools by remember { mutableStateOf<LoadState<List<PlayerPoolResponse>>>(LoadState.Loading) }
  var search by remember { mutableStateOf("") }

  LaunchedEffect(Unit) {
    pools = try {
      coroutineScope {
        val tabs = listOf("official", "community", "mine").map { tab ->
          async { apiGet("/api/player-pools?tab=$tab", ListSerializer(PlayerPoolResponse.serializer())) }
        }
        LoadState.Ready(tabs.awaitAll().flatten().distinctBy { it.id })
      }
    } catch (e: ApiException) {
      LoadState.Failed(e.errorMessage)
    } catch (e: Throwable) {
      LoadState.Failed(UNREACHABLE_MESSAGE)
    }
  }

  Modal(
    open = true,
    title = "Add a whole pool",
    icon = "stack-plus",
    size = ModalSize.Sm,
    onClose = onDismiss,
  ) {
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(12.px).toAttrs()) {
      Input(
        placeholder = "Search pools", icon = "magnifying-glass",
        value = search, onValueChange = { search = it },
      )
      when (val p = pools) {
        is LoadState.Loading -> LoadingSection(size = 36.px, padding = 24.px)
        is LoadState.Failed -> SpanText(
          p.message,
          Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.rust400),
        )
        is LoadState.Ready -> {
          val q = search.trim().lowercase()
          val options = p.value
            .filter { it.id !in excludeIds }
            .filter { q.isEmpty() || it.name.lowercase().contains(q) }
          if (options.isEmpty()) {
            SpanText(
              "No pools to add.",
              Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textMuted),
            )
          }
          options.forEach { pool ->
            Div(
              attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
                .padding(topBottom = 10.px, leftRight = 12.px)
                .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
                .backgroundColor(DmColor.surfaceInset).cursor(Cursor.Pointer)
                .toAttrs { onClick { onPick(pool) } }
            ) {
              Icon(sportIcon(pool.sport), IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.patina300))
              Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
                SpanText(
                  pool.name,
                  Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
                    .color(DmColor.text).displayBlock(),
                )
                SpanText(
                  poolMeta(pool),
                  Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted).displayBlock(),
                )
              }
              if (pool.isOfficial) Badge("Official", tone = BadgeTone.Brass, icon = "seal-check")
              Icon("plus", IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.brass400))
            }
          }
        }
      }
    }
  }
}
